import {promptBridgeApi} from '@dms/api/base';

import {
  ExtractImagesFromZipRequest,
  extractImagesFromZipRequestSchema,
  ExtractImagesFromZipResponse,
  extractImagesFromZipResponseSchema,
} from '@prompt-bridge/shared/validations';

import {
  ExtractDeliveryNoteDocumentRequest,
  extractDeliveryNoteDocumentRequestSchema,
  ExtractDeliveryNoteDocumentResponse,
  extractDeliveryNoteDocumentResponseSchema,
  GetMessageByIdRequest,
  getMessageByIdRequestSchema,
  Message,
  MessageResponse,
  messageResponseSchema,
  GetListMessagesRequest,
  getListMessagesRequestSchema,
  GetListMessagesResponse,
  getListMessagesResponseSchema,
  ScanVINRequest,
  scanVINRequestSchema,
  ScanVINResponse,
  scanVINResponseSchema,
  StorageUploadUrlRequest,
  storageUploadUrlRequestSchema,
  StorageUploadResponse,
  storageUploadResponseSchema,
  StoragePublicUrlRequest,
  storagePublicUrlRequestSchema,
  StoragePublicUrlResponse,
  storagePublicUrlResponseSchema,
  StorageUploadFileRequest,
  storageUploadFileRequestSchema,
  StoragePdfConvertRequest,
  storagePdfConvertRequestSchema,
  StoragePdfConvertResponse,
  storagePdfConvertResponseSchema,
  StorageFileMetadataRequest,
  storageFileMetadataRequestSchema,
  StorageFileMetadataResponse,
  storageFileMetadataResponseSchema,
  StorageOriginalFileRequest,
  storageOriginalFileRequestSchema,
} from './types';

export const promptBridgeExtendedApi = promptBridgeApi.injectEndpoints({
  endpoints: (build) => ({
    extractDeliveryNoteDocument: build.mutation<
      ExtractDeliveryNoteDocumentResponse,
      ExtractDeliveryNoteDocumentRequest
    >({
      query: (queryArg) => ({
        url: `/api/v1/openai/extractDeliveryNoteDocument`,
        method: 'POST',
        body: queryArg,
      }),
      invalidatesTags: [{type: 'PromptBridgeMessage'}],
      extraOptions: {
        requestSchema: extractDeliveryNoteDocumentRequestSchema,
        responseSchema: extractDeliveryNoteDocumentResponseSchema,
      },
    }),
    scanVIN: build.mutation<ScanVINResponse, ScanVINRequest>({
      query: (queryArg) => ({
        url: `/api/v1/openai/scanVIN`,
        method: 'POST',
        body: queryArg,
      }),
      invalidatesTags: [{type: 'PromptBridgeMessage'}],
      extraOptions: {
        requestSchema: scanVINRequestSchema,
        responseSchema: scanVINResponseSchema,
      },
    }),
    getMessages: build.query<GetListMessagesResponse, GetListMessagesRequest>({
      query: (queryArg) => ({
        url: `/api/v1/message/list`,
        method: 'GET',
        params: queryArg,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((message: Message) => ({
                type: 'PromptBridgeMessage' as const,
                id: message.id,
              })),
              {type: 'PromptBridgeMessage'},
            ]
          : [{type: 'PromptBridgeMessage'}],
      extraOptions: {
        requestSchema: getListMessagesRequestSchema,
        responseSchema: getListMessagesResponseSchema,
      },
    }),
    getMessageById: build.query<MessageResponse, GetMessageByIdRequest>({
      query: (queryArg) => ({
        url: `/api/v1/message/${queryArg.id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, queryArg) => [{type: 'PromptBridgeMessage', id: queryArg.id}],
      extraOptions: {
        requestSchema: getMessageByIdRequestSchema,
        responseSchema: messageResponseSchema,
      },
    }),
    uploadFromUrl: build.mutation<StorageUploadResponse, StorageUploadUrlRequest>({
      query: (queryArg) => ({
        url: `/api/v1/storage/upload-from-url`,
        method: 'POST',
        body: queryArg,
      }),
      invalidatesTags: [{type: 'PromptBridgeStorageService'}],
      extraOptions: {
        requestSchema: storageUploadUrlRequestSchema,
        responseSchema: storageUploadResponseSchema,
      },
    }),
    getPublicDownloadUrl: build.query<
      StoragePublicUrlResponse,
      {fileId: string} & StoragePublicUrlRequest
    >({
      query: (queryArg) => ({
        url: `/api/v1/storage/download-url/${queryArg.fileId}`,
        method: 'GET',
        params: {
          namespace: queryArg.namespace,
          filename: queryArg.filename,
          variantId: queryArg.variantId,
          expirationSeconds: queryArg.expirationSeconds,
        },
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'PromptBridgeStorageService', id: queryArg.fileId},
      ],
      extraOptions: {
        requestSchema: storagePublicUrlRequestSchema,
        responseSchema: storagePublicUrlResponseSchema,
      },
    }),
    uploadFile: build.mutation<StorageUploadResponse, {file: File} & StorageUploadFileRequest>({
      query: (queryArg) => {
        const formData = new FormData();
        formData.append('file', queryArg.file);
        if (queryArg.namespace) {
          formData.append('namespace', queryArg.namespace);
        }
        if (queryArg.filename) {
          formData.append('filename', queryArg.filename);
        }
        if (queryArg.webhookUrls) {
          formData.append('webhookUrls', queryArg.webhookUrls);
        }
        if (queryArg.deleteAt) {
          formData.append('deleteAt', queryArg.deleteAt);
        }

        return {
          url: `/api/v1/storage/upload-file`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [{type: 'PromptBridgeStorageService'}],
      extraOptions: {
        requestSchema: storageUploadFileRequestSchema,
        responseSchema: storageUploadResponseSchema,
      },
    }),
    convertPdfToImages: build.mutation<StoragePdfConvertResponse, StoragePdfConvertRequest>({
      query: (queryArg) => ({
        url: `/api/v1/storage/convert-pdf-to-images`,
        method: 'POST',
        body: queryArg,
      }),
      invalidatesTags: [{type: 'PromptBridgeStorageService'}],
      extraOptions: {
        requestSchema: storagePdfConvertRequestSchema,
        responseSchema: storagePdfConvertResponseSchema,
      },
    }),
    getFileMetadata: build.query<
      StorageFileMetadataResponse,
      {fileId: string} & StorageFileMetadataRequest
    >({
      query: (queryArg) => ({
        url: `/api/v1/storage/files/${queryArg.fileId}/metadata`,
        method: 'GET',
        params: {
          namespace: queryArg.namespace,
        },
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'PromptBridgeStorageService', id: queryArg.fileId},
      ],
      extraOptions: {
        requestSchema: storageFileMetadataRequestSchema,
        responseSchema: storageFileMetadataResponseSchema,
      },
    }),
    getOriginalFileUrl: build.query<string, {fileId: string} & StorageOriginalFileRequest>({
      query: (queryArg) => ({
        url: `/api/v1/storage/files/${queryArg.fileId}/original`,
        method: 'GET',
        params: {
          namespace: queryArg.namespace,
        },
        responseHandler: async (response) => {
          const blob = await response.blob();
          return blob.size ? URL.createObjectURL(blob) : null;
        },
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'PromptBridgeStorageService', id: queryArg.fileId},
      ],
      extraOptions: {
        requestSchema: storageOriginalFileRequestSchema,
        // No response schema for binary data
      },
    }),
    extractImagesFromZip: build.query<ExtractImagesFromZipResponse, ExtractImagesFromZipRequest>({
      query: (queryArg) => ({
        url: '/api/v1/storage/extract-images-from-zip',
        method: 'POST',
        body: queryArg,
      }),
      extraOptions: {
        requestSchema: extractImagesFromZipRequestSchema,
        responseSchema: extractImagesFromZipResponseSchema,
      },
    }),
  }),
});

export const {
  useExtractDeliveryNoteDocumentMutation,
  useScanVINMutation,
  useGetMessagesQuery,
  useLazyGetMessagesQuery,
  useGetMessageByIdQuery,
  useLazyGetMessageByIdQuery,
  useUploadFromUrlMutation,
  useGetPublicDownloadUrlQuery,
  useLazyGetPublicDownloadUrlQuery,
  useUploadFileMutation,
  useConvertPdfToImagesMutation,
  useGetFileMetadataQuery,
  useLazyGetFileMetadataQuery,
  useGetOriginalFileUrlQuery,
  useLazyGetOriginalFileUrlQuery,
  useExtractImagesFromZipQuery,
} = promptBridgeExtendedApi;
